import React, { useState, useEffect, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";

import { useStateSelector } from "utils/field";
import { detectModelChanges } from "utils/changeDetection";
import { change } from "store/root/actions";
import { actions as modelActions } from "features/Model/_ducks";

import Button from "components/Button";
import Accordion from "components/Accordion";
import Dialog, { CloseDialog } from "components/Dialog";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import ModelShare from "assets/images/icons/JSX/ModelShare";

const { getAllHistoricalModels } = modelActions;

const VersionHistory = ({ getAllHistoricalModels }) => {
    const dispatch = useDispatch();

    const [versionHistoryList, setVersionHistoryList] = useState([]);
    const [revertDialogOpen, toggleRevertDialog] = useState(false);
    const [reverting, toggleReverting] = useState(false);
    const [revertTo, setRevertTo] = useState({});

    const { modelId = "", modelData: curModelData = {} } = useStateSelector(`model`) || {};

    // Update list of run history and their difference scores (temporary) whenever the current model state is updated
    useEffect(async () => {
        await getAllHistoricalModels(modelId).then((data) => {
            // Sort from most recent to least recent simulation run
            const sortedModelVersions = Object.entries(data).sort(([runIdA, modelStateA], [runIdB, modelStateB]) => {
                return new Date(runIdB) - new Date(runIdA);
            });

            if (!isEmpty(sortedModelVersions)) {
                const scoredModelVersions = sortedModelVersions.reduce((acc, [cliId, prevModel]) => {
                    const { modelData: prevModelData } = prevModel;

                    // Only compare simulation data after this date when data was updated from H2K to SNAP format
                    if (new Date(cliId) > new Date("Oct 7, 2025, 12:07 PM")) {
                        const {
                            changedData = {},
                            diffScore = 0,
                            numAdded = 0,
                            numDeleted = 0,
                        } = detectModelChanges(prevModelData, curModelData);

                        return [...acc, [cliId, { ...prevModel, diffScore, numAdded, numDeleted }]];
                    }

                    return [...acc, [cliId, prevModel]];
                }, []);

                setVersionHistoryList(scoredModelVersions);
            } else {
                setVersionHistoryList(sortedModelVersions);
            }
        });
    }, [curModelData]);

    // Grab data to be reverted to and open confirmation dialog
    const handleRevertClick = useCallback(async (versionTimestamp, versionData) => {
        setRevertTo({ versionTimestamp, versionData });
        toggleRevertDialog(true);
    }, []);

    // Update state with data to be reverted to
    const handleRevertingModel = useCallback(async () => {
        const { modelData = {}, upgradePackage = {} } = revertTo.versionData;

        toggleReverting(true);

        dispatch(change(`model.modelData`, modelData));
        dispatch(change(`upgrades.packages`, upgradePackage));

        toggleReverting(false);
        toggleRevertDialog(false);
    }, [revertTo]);

    return (
        <div className={classes.accordionContainer}>
            <Accordion
                className={sharedClasses.accordion}
                heading={
                    <div className={sharedClasses.accordionIconHeading}>
                        Run Version History <ModelShare />
                    </div>
                }
                large
            >
                <div>
                    {isEmpty(versionHistoryList) ? (
                        <div className={classes.noRunHistory}>No history of simulation runs to display.</div>
                    ) : (
                        versionHistoryList.map(([versionKey, versionData]) => {
                            const versionTimestamp = new Date(versionKey).toLocaleString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                            });

                            return (
                                <div
                                    key={`${versionKey}`}
                                    className={classes.versionContainer}
                                    onClick={() => handleRevertClick(versionTimestamp, versionData)}
                                >
                                    <div> {versionTimestamp} </div>
                                    {/* {(versionData.diffScore >= 0) && (<div> Difference Score: {versionData.diffScore} </div>)} */}
                                    <div className={classes.revertBtn}> Revert Model </div>
                                </div>
                            );
                        })
                    )}
                    <Dialog open={revertDialogOpen}>
                        <CloseDialog closeActions={() => toggleRevertDialog(false)} />
                        <div className={classes.dialog}>
                            <h3>
                                Are you sure you want to revert your model to <br />{" "}
                                <strong>{revertTo.versionTimestamp}</strong>?
                            </h3>
                            <div className={classes.changeSummary}>
                                <p>
                                    <span className={classes.changeTitle}> Packages Included: </span>{" "}
                                    {Object.values(revertTo.versionData?.upgradePackage || {})
                                        .reduce((acc, { name }) => [...acc, name], [])
                                        .join(", ") || "None"}
                                </p>
                                {revertTo.versionData?.numAdded >= 0 && (
                                    <p>
                                        <span className={classes.changeTitle}> Added Since: </span>{" "}
                                        {revertTo.versionData?.numAdded} Component(s)
                                    </p>
                                )}
                                {revertTo.versionData?.numDeleted >= 0 && (
                                    <p>
                                        <span className={classes.changeTitle}> Deleted Since: </span>{" "}
                                        {revertTo.versionData?.numDeleted} Component(s)
                                    </p>
                                )}
                            </div>
                            <div className={classes.footer}>
                                <Button type="hollow" onClick={() => toggleRevertDialog(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleRevertingModel} type="red" disabled={reverting}>
                                    {reverting ? "Reverting..." : "Revert"}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </div>
            </Accordion>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    getAllHistoricalModels: (modelId) => dispatch(getAllHistoricalModels(modelId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(VersionHistory);
